import { useEffect, useRef } from 'react'
import { Sparkles, Loader2 } from 'lucide-react'
import type { SuggestionDraft } from '@/api/client'

interface Props {
  draft: SuggestionDraft
}

/**
 * Renders an in-flight suggestion as the LLM tokens stream in. Replaced by a
 * proper {@link SuggestionCard} once the BFF broadcasts the `ready` frame for
 * this auditCheckId.
 */
export default function StreamingSuggestionCard({ draft }: Props) {
  // Auto-scroll the preview pane as new text arrives so the user always sees
  // the latest tokens without manual scrolling.
  const preRef = useRef<HTMLPreElement | null>(null)
  useEffect(() => {
    const el = preRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [draft.text])

  return (
    <div
      className="glass rounded-xl overflow-hidden"
      style={{ border: '1px solid var(--border-subtle)' }}
    >
      <div className="flex items-start gap-3 px-5 py-3"
        style={{ background: 'rgba(99,102,241,0.05)' }}>
        <Sparkles size={14} className="mt-0.5 shrink-0 text-accent" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold">
              {draft.auditTitle ?? draft.auditCheckId}
            </span>
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
              style={{
                color: 'var(--accent)',
                background: 'rgba(99,102,241,0.15)',
                border: '1px solid rgba(99,102,241,0.4)',
              }}
            >
              <Loader2 size={9} className="animate-spin" />
              Generating
            </span>
          </div>
        </div>
      </div>

      <pre
        ref={preRef}
        className="text-[11px] font-mono leading-relaxed overflow-y-auto max-h-48 px-5 py-3 m-0"
        style={{ color: 'var(--text-secondary)', background: 'rgba(0,0,0,0.2)' }}
      >
        <code>
          {draft.text || <span style={{ color: 'var(--text-muted)' }}>Waiting for tokens…</span>}
          <span className="inline-block w-1.5 h-3 bg-accent ml-0.5 align-middle animate-pulse" />
        </code>
      </pre>
    </div>
  )
}
